import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CandidatePageShell from "./CandidatePageShell";
import useFetch from "../../useFetch";
import { apiRequest } from "../../utils/api";

const defaultForm = {
  title: "",
  keyword: "",
  location: "",
  frequency: "Daily",
  is_active: true,
};

const CandidateJobAlerts = () => {
  const [apiUrl, setApiUrl] = useState(process.env.REACT_APP_BASE_API_URL + "/candidate/job-alerts");
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [editingAlert, setEditingAlert] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const { response, isLoading, error: fetchError } = useFetch(apiUrl);

  useEffect(() => {
    setAlerts(response?.job_alerts ?? []);
  }, [response]);

  const refresh = () => {
    setApiUrl(process.env.REACT_APP_BASE_API_URL + "/candidate/job-alerts?_=" + Date.now());
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const resetForm = () => {
    setForm(defaultForm);
    setEditingAlert(null);
  };

  const editAlert = (alert) => {
    setEditingAlert(alert);
    setForm({
      title: alert.title ?? "",
      keyword: alert.keyword ?? "",
      location: alert.location ?? "",
      frequency: alert.frequency ?? "Daily",
      is_active: Boolean(alert.is_active),
    });
  };

  const submitAlert = (e) => {
    e.preventDefault();

    setIsSaving(true);
    setSuccess(false);
    setError(false);
    setValidationErrors([]);

    let formData = new FormData();
    formData.append("title", form.title);
    formData.append("keyword", form.keyword);
    formData.append("location", form.location);
    formData.append("frequency", form.frequency);
    formData.append("is_active", form.is_active ? "1" : "0");

    apiRequest(editingAlert ? `/candidate/job-alerts/${editingAlert.id}/update` : "/candidate/job-alerts", {
      method: "POST",
      body: formData,
    })
      .then(data => {
        setIsSaving(false);

        if (data?.status === "success") {
          setSuccess(data.message);
          resetForm();
          refresh();
        }
        else if (data?.status === "validation_error") {
          setValidationErrors(data.messages);
        }
        else if (data?.status === "error" && data?.message) {
          setError(data.message);
        }
        else {
          setError("There was some error while saving job alert, please try again.");
        }
      })
      .catch(error => {
        setIsSaving(false);
        if (error.data?.status === "validation_error") {
          setValidationErrors(error.messages);
        }
        else {
          setError(error.message);
        }
      });
  };

  const removeAlert = (alert) => {
    apiRequest(`/candidate/job-alerts/${alert.id}`, {
      method: "DELETE",
    })
      .then(data => {
        setSuccess(data?.message ?? "Job alert removed successfully.");
        refresh();
      })
      .catch(() => refresh());
  };

  return (
    <CandidatePageShell
      active="alerts"
      title="Job Alerts"
      text="Saved searches that help you find matching jobs faster."
    >
      <div className="ls-widget">
        <div className="widget-title">
          <h4>{editingAlert ? "Edit Alert" : "Create Alert"}</h4>
          <Link to="/jobs" className="theme-btn btn-style-three">Browse Jobs</Link>
        </div>
        <div className="widget-content">
          {success && <div className="alert alert-success"><strong>{success}</strong></div>}
          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {validationErrors.length > 0 && (
            <div className="alert alert-danger">
              <strong>There was some error in submitted information, please try again.</strong>
              <ul className="mb-0 mt-2">
                {validationErrors.map((validationError, index) => (
                  <li key={index}>{validationError}</li>
                ))}
              </ul>
            </div>
          )}

          <form className="default-form" onSubmit={submitAlert}>
            <div className="row">
              <div className="form-group col-lg-4 col-md-12">
                <label>Alert Title</label>
                <input type="text" name="title" placeholder="Frontend roles" value={form.title} onChange={handleChange} required />
              </div>
              <div className="form-group col-lg-3 col-md-12">
                <label>Keyword</label>
                <input type="text" name="keyword" placeholder="React" value={form.keyword} onChange={handleChange} />
              </div>
              <div className="form-group col-lg-3 col-md-12">
                <label>Location</label>
                <input type="text" name="location" placeholder="Remote" value={form.location} onChange={handleChange} />
              </div>
              <div className="form-group col-lg-2 col-md-12">
                <label>Frequency</label>
                <select name="frequency" value={form.frequency} onChange={handleChange}>
                  <option>Daily</option>
                  <option>Weekly</option>
                  <option>Monthly</option>
                </select>
              </div>
              <div className="form-group col-lg-12">
                <button type="submit" className="theme-btn btn-style-one" disabled={isSaving}>
                  {isSaving ? "Loading..." : editingAlert ? "Update Alert" : "Save Alert"}
                </button>
                {editingAlert && (
                  <button type="button" className="theme-btn btn-style-three" style={{ marginLeft: 12 }} onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="ls-widget">
        <div className="widget-title">
          <h4>Saved Alerts</h4>
        </div>
        <div className="widget-content">
          {fetchError && <div className="alert alert-danger"><strong>{fetchError}</strong></div>}
          {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

          <div className="candidate-alert-list">
            {!fetchError && !isLoading && alerts.map((alert) => (
              <div className="candidate-alert-item" key={alert.id}>
                <div>
                  <h5>{alert.title}</h5>
                  <p>{[alert.keyword, alert.location].filter(Boolean).join(" - ") || "All jobs"} - {alert.frequency} alerts</p>
                </div>
                <span>{alert.matches_count ?? 0} matches</span>
                <div className="candidate-alert-actions">
                  <button type="button" onClick={() => editAlert(alert)}>Edit</button>
                  <button type="button" onClick={() => removeAlert(alert)}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          {!fetchError && !isLoading && alerts.length === 0 && (
            <div className="employee-empty-state">No job alerts saved yet.</div>
          )}
        </div>
      </div>
    </CandidatePageShell>
  );
};

export default CandidateJobAlerts;
